import { Action, createReducer, on } from '@ngrx/store';
import {
    clearLocationTrackerSaveMessage,
    clearPrivacyPerimeter,
    locationTrackerDraftSeeded,
    privacyPerimeterRequestFailed,
    privacyPerimeterResolved,
    requestPrivacyPerimeter,
    saveLocationTrackerUserConfig,
    saveLocationTrackerUserConfigFailed,
    saveLocationTrackerUserConfigSuccess,
    updateLocationTrackerUserConfig,
} from '../actions/location-tracker.actions';
import {
    initialLocationTrackerState,
    LocationTrackerErrorKey,
    LocationTrackerSaveStatus,
    LocationTrackerState,
    LocationTrackerUserConfigState,
    PRIVACY_PERIMETER_PRECISION_FACTOR,
} from '../location-tracker.state';

/** Seeds the draft from the user store, once, the first time the seed effect resolves it. */
export function seedLocationTrackerDraftReducer(
    state: Readonly<LocationTrackerState>,
    { config }: { config: LocationTrackerUserConfigState },
): Readonly<LocationTrackerState> {
    return { ...state, draft: config };
}

/**
 * Applies one form edit. Deliberately does not touch `saveStatus` — faithful to the old
 * component, where the save message only ever cleared on its own 5 s timer, never on the next edit.
 */
export function applyConfigEditReducer(
    state: Readonly<LocationTrackerState>,
    { config }: { config: LocationTrackerUserConfigState },
): Readonly<LocationTrackerState> {
    return { ...state, draft: config };
}

/** Marks a geolocation lookup as in flight and clears any stale error left over from a previous attempt. */
export function startPerimeterRequestReducer(state: Readonly<LocationTrackerState>): Readonly<LocationTrackerState> {
    return { ...state, isPerimeterRequestPending: true, perimeterErrorKey: undefined };
}

/**
 * Owns the privacy truncation: raw coordinates are truncated — not rounded — to
 * {@link PRIVACY_PERIMETER_PRECISION_FACTOR} precision before they ever reach the draft,
 * mirroring `LocationTrackerModel.setPrivacyPerimeterLocation`.
 *
 * A no-op on `draft` itself when `draft` is still `undefined`: the perimeter picker is only
 * reachable once the page has rendered the form, which only happens once the draft has been
 * seeded, so this should never actually run against `undefined` — but the type allows it, and
 * silently dropping the coordinates is safer than throwing. `isPerimeterRequestPending` is still
 * cleared either way, so the UI never gets stuck showing a lookup as pending.
 */
export function applyResolvedPerimeterReducer(
    state: Readonly<LocationTrackerState>,
    { latitude, longitude }: { latitude: number; longitude: number },
): Readonly<LocationTrackerState> {
    return {
        ...state,
        draft: state.draft && {
            ...state.draft,
            privacyPerimeterLatitude:
                Math.trunc(latitude * PRIVACY_PERIMETER_PRECISION_FACTOR) / PRIVACY_PERIMETER_PRECISION_FACTOR,
            privacyPerimeterLongitude:
                Math.trunc(longitude * PRIVACY_PERIMETER_PRECISION_FACTOR) / PRIVACY_PERIMETER_PRECISION_FACTOR,
        },
        isPerimeterRequestPending: false,
    };
}

/** Records why the geolocation lookup failed and clears the in-flight flag. */
export function applyPerimeterErrorReducer(
    state: Readonly<LocationTrackerState>,
    { errorKey }: { errorKey: LocationTrackerErrorKey },
): Readonly<LocationTrackerState> {
    return { ...state, perimeterErrorKey: errorKey, isPerimeterRequestPending: false };
}

/**
 * Clears both coordinates and any lookup error. A no-op on `draft` when it is still `undefined` —
 * see `applyResolvedPerimeterReducer`'s doc comment for why that path should not occur in practice.
 */
export function clearPrivacyPerimeterReducer(state: Readonly<LocationTrackerState>): Readonly<LocationTrackerState> {
    return {
        ...state,
        draft: state.draft && {
            ...state.draft,
            privacyPerimeterLatitude: undefined,
            privacyPerimeterLongitude: undefined,
        },
        perimeterErrorKey: undefined,
    };
}

/** Marks a save as in flight. */
export function setSavingReducer(state: Readonly<LocationTrackerState>): Readonly<LocationTrackerState> {
    return { ...state, saveStatus: LocationTrackerSaveStatus.Saving };
}

/** Marks a save as succeeded, so the page can show the success message. */
export function setSaveSucceededReducer(state: Readonly<LocationTrackerState>): Readonly<LocationTrackerState> {
    return { ...state, saveStatus: LocationTrackerSaveStatus.Saved };
}

/** Marks a save as failed, so the page can show the error message. */
export function setSaveFailedReducer(state: Readonly<LocationTrackerState>): Readonly<LocationTrackerState> {
    return { ...state, saveStatus: LocationTrackerSaveStatus.Failed };
}

/** Hides the save confirmation/error once its 5 s timer (see `SAVE_MESSAGE_VISIBLE_MS`) has elapsed. */
export function clearSaveMessageReducer(state: Readonly<LocationTrackerState>): Readonly<LocationTrackerState> {
    return { ...state, saveStatus: LocationTrackerSaveStatus.Idle };
}

const createLocationTrackerReducer = createReducer(
    initialLocationTrackerState,
    on(locationTrackerDraftSeeded, seedLocationTrackerDraftReducer),
    on(updateLocationTrackerUserConfig, applyConfigEditReducer),
    on(requestPrivacyPerimeter, startPerimeterRequestReducer),
    on(privacyPerimeterResolved, applyResolvedPerimeterReducer),
    on(privacyPerimeterRequestFailed, applyPerimeterErrorReducer),
    on(clearPrivacyPerimeter, clearPrivacyPerimeterReducer),
    on(saveLocationTrackerUserConfig, setSavingReducer),
    on(saveLocationTrackerUserConfigSuccess, setSaveSucceededReducer),
    on(saveLocationTrackerUserConfigFailed, setSaveFailedReducer),
    on(clearLocationTrackerSaveMessage, clearSaveMessageReducer),
);

export function locationTrackerReducer(
    state: Readonly<LocationTrackerState> | undefined,
    action: Action,
): Readonly<LocationTrackerState> {
    return createLocationTrackerReducer(state, action);
}
